import React, { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Send, Plus, CheckCircle2, XCircle, Clock, Loader2, X, ChevronRight, ExternalLink, Copy, Image, Video } from 'lucide-react';

const PLATFORM_ICONS = {
  instagram: 'https://cdn.simpleicons.org/instagram/E4405F',
  tiktok: 'https://cdn.simpleicons.org/tiktok/ffffff',
  facebook: 'https://cdn.simpleicons.org/facebook/1877F2',
  youtube: 'https://cdn.simpleicons.org/youtube/FF0000',
  'google-ads': 'https://cdn.simpleicons.org/googleads/4285F4',
  linkedin: 'https://cdn.simpleicons.org/linkedin/0A66C2',
  'twitter-x': 'https://cdn.simpleicons.org/x/ffffff',
  pinterest: 'https://cdn.simpleicons.org/pinterest/BD081C',
  email: null,
  snapchat: 'https://cdn.simpleicons.org/snapchat/FFFC00',
};

export default function DistributePage() {
  const [distributions, setDistributions] = useState([]);
  const [platforms, setPlatforms] = useState({});
  const [generations, setGenerations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedDist, setSelectedDist] = useState(null);

  const [form, setForm] = useState({ generation_ids: [], platforms: [], caption: '', hashtags: '', campaign_name: '' });
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      const [d, p] = await Promise.all([api.listDistributions(20), api.getDistributionPlatforms()]);
      setDistributions(d);
      setPlatforms(p);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = async () => {
    try {
      const gens = await api.listGenerations({ status: 'completed', limit: 30 });
      setGenerations(gens.filter(g => g.output_url));
      setForm({ generation_ids: [], platforms: [], caption: '', hashtags: '', campaign_name: '' });
      setShowCreate(true);
    } catch { toast.error('Failed to load generations'); }
  };

  const toggleGen = (id) => {
    setForm(f => ({ ...f, generation_ids: f.generation_ids.includes(id) ? f.generation_ids.filter(x => x !== id) : [...f.generation_ids, id] }));
  };

  const togglePlatform = (key) => {
    setForm(f => ({ ...f, platforms: f.platforms.includes(key) ? f.platforms.filter(x => x !== key) : [...f.platforms, key] }));
  };

  const handleCreate = async () => {
    if (form.generation_ids.length === 0) { toast.error('Select at least one creative'); return; }
    if (form.platforms.length === 0) { toast.error('Select at least one platform'); return; }
    setCreating(true);
    try {
      const data = { ...form, hashtags: form.hashtags ? form.hashtags.split(',').map(h => h.trim()).filter(Boolean) : [] };
      const dist = await api.createDistribution(data);
      toast.success(`Deploying to ${form.platforms.length} platforms!`);
      setShowCreate(false);
      // Poll for completion
      setTimeout(async () => {
        try {
          const updated = await api.getDistribution(dist.id);
          setSelectedDist(updated);
        } catch {}
        load();
      }, 4000);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Distribution failed'); }
    finally { setCreating(false); }
  };

  const loadDetail = async (id) => {
    try { setSelectedDist(await api.getDistribution(id)); } catch {}
  };

  const statusColor = (s) => s === 'deployed' ? 'text-green-400' : s === 'failed' ? 'text-red-400' : 'text-yellow-400';
  const statusBg = (s) => s === 'completed' ? 'bg-green-500/10 text-green-400' : s === 'partial' ? 'bg-yellow-500/10 text-yellow-400' : s === 'processing' ? 'bg-blue-500/10 text-blue-400' : 'bg-white/5 text-white/40';

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#CDFE65] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="animate-fade-in-up opacity-0" data-testid="distribute-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-black tracking-tight" style={{ fontFamily: 'Chivo' }}>
            Distribute <span className="text-[#06B6D4]">Everywhere</span>
          </h1>
          <p className="text-white/40 mt-1">Deploy your creatives to every platform in one click</p>
        </div>
        <button onClick={openCreate} className="btn-primary text-sm flex items-center gap-2" data-testid="create-distribution-btn">
          <Send className="w-4 h-4" /> New Distribution
        </button>
      </div>

      {/* Platform Overview */}
      <div className="grid grid-cols-5 md:grid-cols-10 gap-2 mb-8" data-testid="platform-overview">
        {Object.entries(platforms).map(([key, plat]) => (
          <div key={key} className="bg-[#0F0F11] border border-white/5 rounded-xl p-3 text-center card-hover" data-testid={`platform-icon-${key}`}>
            {PLATFORM_ICONS[key] ? (
              <img src={PLATFORM_ICONS[key]} alt={plat.name} className="w-6 h-6 mx-auto mb-1.5 opacity-60" loading="lazy" />
            ) : (
              <Send className="w-6 h-6 mx-auto mb-1.5 text-white/30" />
            )}
            <p className="text-[9px] text-white/40 font-medium truncate">{plat.name}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Distribution List */}
        <div className="lg:col-span-4 space-y-3" data-testid="distribution-list">
          {distributions.length === 0 ? (
            <div className="bg-[#0F0F11] border border-white/5 rounded-xl p-10 text-center">
              <Send className="w-10 h-10 text-white/5 mx-auto mb-3" />
              <p className="text-sm text-white/30">No distributions yet</p>
              <button onClick={openCreate} className="btn-primary text-xs mt-4">Create First Distribution</button>
            </div>
          ) : distributions.map(dist => (
            <div key={dist.id} onClick={() => loadDetail(dist.id)} className={`bg-[#0F0F11] border rounded-xl p-4 cursor-pointer card-hover ${selectedDist?.id === dist.id ? 'border-[#06B6D4]' : 'border-white/5'}`} data-testid={`dist-card-${dist.id}`}>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-bold text-sm truncate">{dist.campaign_name}</h3>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${statusBg(dist.status)}`}>{dist.status}</span>
              </div>
              <div className="flex items-center gap-1.5 mb-2">
                {(dist.platforms || []).slice(0, 5).map(p => (
                  PLATFORM_ICONS[p] ? <img key={p} src={PLATFORM_ICONS[p]} alt={p} className="w-4 h-4 opacity-50" /> : <Send key={p} className="w-3.5 h-3.5 text-white/20" />
                ))}
                {(dist.platforms || []).length > 5 && <span className="text-[10px] text-white/30">+{dist.platforms.length - 5}</span>}
              </div>
              <div className="flex items-center gap-3 text-[10px] text-white/30 font-mono">
                <span>{dist.total_deployments} deployments</span>
                <span className="text-green-400">{dist.completed_deployments} live</span>
                {dist.failed_deployments > 0 && <span className="text-red-400">{dist.failed_deployments} failed</span>}
              </div>
            </div>
          ))}
        </div>

        {/* Distribution Detail */}
        <div className="lg:col-span-8" data-testid="distribution-detail">
          {selectedDist ? (
            <div className="space-y-4">
              <div className="bg-[#0F0F11] border border-white/5 rounded-xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-xl font-bold">{selectedDist.campaign_name}</h2>
                    <p className="text-xs text-white/30 font-mono mt-1">{selectedDist.created_at?.slice(0, 16)} · {selectedDist.platforms?.length} platforms · {selectedDist.generation_ids?.length} creatives</p>
                  </div>
                  <span className={`text-xs px-3 py-1 rounded-full font-bold uppercase ${statusBg(selectedDist.status)}`}>{selectedDist.status}</span>
                </div>

                {selectedDist.caption && (
                  <div className="bg-white/[0.02] rounded-lg p-3 mb-4">
                    <p className="text-xs text-white/30 uppercase tracking-wider mb-1">Caption</p>
                    <p className="text-sm text-white/70">{selectedDist.caption}</p>
                    {selectedDist.hashtags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {selectedDist.hashtags.map(h => <span key={h} className="text-[10px] text-[#06B6D4]">#{h}</span>)}
                      </div>
                    )}
                  </div>
                )}

                {/* Stats */}
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="bg-white/[0.02] rounded-lg p-3 text-center">
                    <div className="text-xl font-black" style={{ fontFamily: 'Chivo' }}>{selectedDist.total_deployments}</div>
                    <p className="text-[10px] text-white/30 uppercase">Total</p>
                  </div>
                  <div className="bg-white/[0.02] rounded-lg p-3 text-center">
                    <div className="text-xl font-black text-green-400" style={{ fontFamily: 'Chivo' }}>{selectedDist.completed_deployments}</div>
                    <p className="text-[10px] text-white/30 uppercase">Deployed</p>
                  </div>
                  <div className="bg-white/[0.02] rounded-lg p-3 text-center">
                    <div className="text-xl font-black text-red-400" style={{ fontFamily: 'Chivo' }}>{selectedDist.failed_deployments}</div>
                    <p className="text-[10px] text-white/30 uppercase">Failed</p>
                  </div>
                </div>
              </div>

              {/* Deployment Grid grouped by platform */}
              <div className="space-y-3">
                {(selectedDist.platforms || []).map(platKey => {
                  const plat = platforms[platKey];
                  const deps = (selectedDist.deployments || []).filter(d => d.platform === platKey);
                  return (
                    <div key={platKey} className="bg-[#0F0F11] border border-white/5 rounded-xl p-4">
                      <div className="flex items-center gap-3 mb-3">
                        {PLATFORM_ICONS[platKey] ? <img src={PLATFORM_ICONS[platKey]} alt={platKey} className="w-5 h-5" /> : <Send className="w-5 h-5 text-white/30" />}
                        <h3 className="font-bold text-sm">{plat?.name || platKey}</h3>
                        <span className="text-[10px] text-white/30 font-mono">{deps.length} formats</span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                        {deps.map((dep, i) => (
                          <div key={i} className="flex items-center justify-between p-2.5 bg-white/[0.02] rounded-lg">
                            <div className="flex items-center gap-2 min-w-0">
                              <div className={`w-1.5 h-1.5 rounded-full ${dep.status === 'deployed' ? 'bg-green-400' : dep.status === 'failed' ? 'bg-red-400' : 'bg-yellow-400'}`} />
                              <div className="min-w-0">
                                <p className="text-xs font-medium truncate">{dep.format_type}</p>
                                <p className="text-[10px] text-white/30 font-mono">{dep.target_size}</p>
                              </div>
                            </div>
                            {dep.external_url && (
                              <a href={dep.external_url} target="_blank" rel="noopener noreferrer" className="text-white/20 hover:text-[#06B6D4] shrink-0">
                                <ExternalLink className="w-3.5 h-3.5" />
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="bg-[#0F0F11] border border-white/5 rounded-xl p-16 text-center">
              <Send className="w-12 h-12 text-white/5 mx-auto mb-4" />
              <p className="text-white/20">Select a distribution or create a new one</p>
            </div>
          )}
        </div>
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="create-distribution-modal">
          <div className="bg-[#0F0F11] border border-white/10 rounded-2xl p-6 w-full max-w-2xl mx-4 max-h-[85vh] overflow-y-auto animate-fade-in-up opacity-0">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">New Distribution</h2>
              <button onClick={() => setShowCreate(false)} className="text-white/30 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-5">
              <div>
                <label className="text-xs uppercase tracking-wider text-white/40 font-mono mb-1.5 block">Campaign Name</label>
                <input value={form.campaign_name} onChange={e => setForm(f => ({ ...f, campaign_name: e.target.value }))} className="forge-input w-full" placeholder="Q1 Product Launch" data-testid="campaign-name-input" />
              </div>

              {/* Select Creatives */}
              <div>
                <label className="text-xs uppercase tracking-wider text-white/40 font-mono mb-2 block">Select Creatives ({form.generation_ids.length} selected)</label>
                <div className="grid grid-cols-4 md:grid-cols-6 gap-2 max-h-[180px] overflow-y-auto">
                  {generations.map(g => (
                    <div key={g.id} onClick={() => toggleGen(g.id)} className={`relative rounded-lg overflow-hidden border cursor-pointer transition-all ${form.generation_ids.includes(g.id) ? 'border-[#06B6D4] ring-1 ring-[#06B6D4]/30' : 'border-white/5 hover:border-white/20'}`} data-testid={`select-gen-${g.id}`}>
                      <img src={g.output_url} alt="" className="w-full aspect-square object-cover" loading="lazy" />
                      {form.generation_ids.includes(g.id) && <div className="absolute top-1 right-1 w-4 h-4 bg-[#06B6D4] rounded-full flex items-center justify-center"><CheckCircle2 className="w-3 h-3 text-white" /></div>}
                    </div>
                  ))}
                </div>
              </div>

              {/* Select Platforms */}
              <div>
                <label className="text-xs uppercase tracking-wider text-white/40 font-mono mb-2 block">Deploy to ({form.platforms.length} platforms)</label>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  {Object.entries(platforms).map(([key, plat]) => (
                    <button key={key} onClick={() => togglePlatform(key)} className={`flex items-center gap-2 p-2.5 rounded-lg border text-xs font-medium transition-all ${form.platforms.includes(key) ? 'border-[#06B6D4] bg-[#06B6D4]/5 text-white' : 'border-white/5 text-white/40 hover:border-white/20'}`} data-testid={`select-platform-${key}`}>
                      {PLATFORM_ICONS[key] ? <img src={PLATFORM_ICONS[key]} alt={key} className="w-4 h-4" /> : <Send className="w-4 h-4" />}
                      {plat.name}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs uppercase tracking-wider text-white/40 font-mono mb-1.5 block">Caption</label>
                <textarea value={form.caption} onChange={e => setForm(f => ({ ...f, caption: e.target.value }))} rows={2} className="forge-input w-full h-auto py-3 resize-none" placeholder="Your ad copy..." data-testid="caption-input" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-white/40 font-mono mb-1.5 block">Hashtags (comma separated)</label>
                <input value={form.hashtags} onChange={e => setForm(f => ({ ...f, hashtags: e.target.value }))} className="forge-input w-full" placeholder="ai, marketing, product" data-testid="hashtags-input" />
              </div>

              <button onClick={handleCreate} disabled={creating} className="btn-primary w-full text-sm flex items-center justify-center gap-2 py-3" data-testid="submit-distribution-btn">
                {creating ? <><Loader2 className="w-4 h-4 animate-spin" /> Deploying...</> : <><Send className="w-4 h-4" /> Deploy to {form.platforms.length} Platforms</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
